import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import ListEmptyState from '../../components/list/ListEmptyState';
import ListLoadingState from '../../components/list/ListLoadingState';
import { ShortCardGrid } from '../../components/list/ShortCard';
import { CampusShortCard } from '../../components/list/ShortCardVariants';
import AppSelect from '../../components/ui/AppSelect'

function CampusesList({
  campuses = [],
  profile = null,
  currentCampusId,
  canSwitchCampus = false,
  campusOptions = [],
  isOrgWideAdmin = false,
  onCampusChange,
  viewAllTo = '/admin/campuses',
  isLoading = false,
}) {
  const activeCampuses = Array.isArray(campuses)
    ? campuses.filter((c) => c && c.is_active)
    : [];

  const context = useMemo(() => {
    const campusNameFromProfile = profile?.campus_name;
    const resolvedId = currentCampusId ?? null;
    const resolvedName =
      campusNameFromProfile ||
      activeCampuses.find((c) => String(c.id) === String(resolvedId))?.name ||
      campusOptions.find((o) => String(o.id) === String(resolvedId))?.campus_name ||
      null;

    const isCampusAdmin = profile?.user_type === 'campus_admin';
    const multiOrganisation = activeCampuses.length > 1;

    if (canSwitchCampus && campusOptions.length > 1) {
      return {
        mode: 'switcher',
        subtitle: isCampusAdmin
          ? 'Switch your working context for student approvals.'
          : 'Choose which organisation you are acting on right now.',
        label: isCampusAdmin ? 'Working campus' : 'Active context',
        resolvedId,
        resolvedName,
      };
    }

    if (resolvedName) {
      return {
        mode: 'single',
        subtitle: isCampusAdmin
          ? 'Your assigned organisation for day-to-day operations.'
          : 'Your selected organisation context.',
        label: isCampusAdmin ? 'Your campus' : 'Active context',
        resolvedId,
        resolvedName,
      };
    }

    if (multiOrganisation && isOrgWideAdmin) {
      return {
        mode: 'overview',
        subtitle: 'You manage learners across multiple organisations — pick a card to focus.',
        label: 'Organisations',
        count: activeCampuses.length,
      };
    }

    if (activeCampuses.length === 1) {
      return {
        mode: 'single',
        subtitle: 'Your organisation on Learnly.',
        label: 'Organisation',
        resolvedId: activeCampuses[0].id,
        resolvedName: activeCampuses[0].name,
      };
    }

    return {
      mode: 'empty',
      subtitle: 'Campuses you manage will appear here.',
      label: 'Organisations',
    };
  }, [
    activeCampuses,
    campusOptions,
    canSwitchCampus,
    currentCampusId,
    isOrgWideAdmin,
    profile?.campus_name,
    profile?.user_type,
  ]);

  return (
    <section className="campus-section">
      <div className="campus-section-header">
        <div className="campus-section-intro">
          <h3 className="section-title">Campuses</h3>
          <p className="campus-section-subtitle">{context.subtitle}</p>
        </div>

        <div className="campus-section-actions">
          {context.mode === 'switcher' && (
            <label className="campus-context-control">
              <span className="campus-context-label">{context.label}</span>
              <div className="campus-context-select-wrap">
                <AppSelect
                  className="campus-context-select"
                  value={context.resolvedId ?? ''}
                  onChange={(e) => onCampusChange?.(Number(e.target.value))}
                  aria-label={context.label}
                >
                  {campusOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.campus_name || o.name || `Campus #${o.id}`}
                    </option>
                  ))}
                </AppSelect>
              </div>
            </label>
          )}

          {context.mode === 'single' && (
            <div className="campus-context-pill" title={context.resolvedName}>
              <span className="campus-context-label">{context.label}</span>
              <span className="campus-context-value">{context.resolvedName}</span>
            </div>
          )}

          {context.mode === 'overview' && (
            <div className="campus-context-pill campus-context-pill--overview">
              <span className="campus-context-label">{context.label}</span>
              <span className="campus-context-value">
                <Building2 size={16} aria-hidden />
                {context.count} active
              </span>
            </div>
          )}

          <Link to={viewAllTo} className="ux-btn-sm ux-btn-ghost campus-section-view-all">
            View all
          </Link>
        </div>
      </div>

      {isLoading ? (
        <ListLoadingState label="Loading campuses…" />
      ) : activeCampuses.length === 0 ? (
        <ListEmptyState icon={Building2} title="No active campuses" description="No active campuses were found." />
      ) : (
        <ShortCardGrid>
          {activeCampuses.slice(0, 4).map((c, index) => (
            <CampusShortCard
              key={c.id}
              campus={c}
              index={index}
              isCurrent={context.resolvedId != null && String(c.id) === String(context.resolvedId)}
            />
          ))}
        </ShortCardGrid>
      )}
    </section>
  );
}

export default CampusesList;
